import React, { useState, useEffect } from 'react';
import { getAllBuildingTypes, uploadSpace } from '../api/spaceApi';
import { getAllTag } from '../api/tagApi';
import { getAllRegions } from '../api/regionApi';
import { useNavigate } from 'react-router-dom';
import Logo from '../componets/Logo';
import imageCompression from 'browser-image-compression';
import { Helmet } from 'react-helmet-async';

interface SpaceFormState {
  spaceName: string;
  description: string;
  location: string;
  pricePerDay: number;
  maxDurationDays: number;
  main_image: File | null;
  additional_images: File[];
  tags: number[]; // 선택된 태그 ID 배열
  regionId: number | null; // 선택된 지역 ID 추가
  region: number
  buildingTypeId: number | null;
}

const UploadSpace: React.FC = () => {
  const navigate = useNavigate();

  const [formState, setFormState] = useState<SpaceFormState>({
    spaceName: '',
    description: `예약안내

공간에 대한 상세사항을 적어주세요

연락처 : (개별간 거래 시 에스크로 및 보험 안전서비스가 불가합니다.)

공간 특징 : `,
    location: '',
    pricePerDay: 3000,
    maxDurationDays: 30,
    main_image: null,
    additional_images: [],
    tags: [],
    regionId: null,
    region: 0,
    buildingTypeId: null,
  });

  const [availableTags, setAvailableTags] = useState<{ id: number; name: string }[]>([]);
  const [availableRegions, setAvailableRegions] = useState<{ id: number; name: string }[]>([]);
  const [buildingType, setBuildingType] = useState<{ id: number; name: string }[]>([]);
  
  // 공간 특징 버튼들
  const spaceFeatures = [
    '보안씰',
    '냉온장보관',
    '보관면적',
    '픽업 가능',
    '장기보관 할인'
  ];

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await getAllTag();
        const data = await response.json();
        setAvailableTags(data.body.tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    const fetchRegions = async () => {
      try {
        const response = await getAllRegions();
        const data = await response;
        setAvailableRegions(data.body.regions);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };

    const fetchBuilidingType = async () => {
      try {
        const response = await getAllBuildingTypes();
        const data = await response
        setBuildingType(data.body.buildingTypes);
      } catch (error) {
        console.error('Error fetching building types:', error);
      }
    };

    fetchTags();
    fetchRegions();
    fetchBuilidingType();
  }, []);

  const handleMainImageChange = async (files: FileList | null) => {
    if (!files) return;
    try {
      const compressedBlob = await imageCompression(files[0], {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });
  
      const compressedFile = new File([compressedBlob], files[0].name, { type: compressedBlob.type });
  
      setFormState((prev) => ({
        ...prev,
        main_image: compressedFile,
      }));
    } catch (error) {
      console.error('이미지 업로드 중 오류 발생:', error);
      alert('이미지 처리 중 오류가 발생했습니다.');
    }
  };
  
  // 추가 이미지 (최대 3장)
  const handleAdditionalImagesChange = async (files: FileList | null) => {
    if (!files) return;
  
    try {
      const compressedFiles: File[] = [];
  
      for (let i = 0; i < Math.min(files.length, 3); i++) {
        const compressedBlob = await imageCompression(files[i], {
          maxSizeMB: 0.8,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        });
        const compressedFile = new File([compressedBlob], files[i].name, { type: compressedBlob.type });
        compressedFiles.push(compressedFile);
      }
  
      setFormState((prev) => ({
        ...prev,
        additional_images: compressedFiles,
      }));
    } catch (error) {
      console.error('추가 이미지 업로드 오류:', error);
      alert('추가 이미지 처리 중 오류가 발생했습니다.');
    }
  };

  const handleTagSelection = (tagId: number) => {
    setFormState((prev) => {
      const isSelected = prev.tags.includes(tagId);
      return {
        ...prev,
        tags: isSelected ? prev.tags.filter((id) => id !== tagId) : [...prev.tags, tagId],
      };
    });
  };

  // 공간 특징 버튼 클릭 핸들러
  const handleFeatureClick = (feature: string) => {
    setFormState((prev) => {
      const currentDescription = prev.description;
      const featureLineIndex = currentDescription.indexOf('공간 특징 : ');
      
      if (featureLineIndex === -1) return prev;
      
      const beforeFeature = currentDescription.substring(0, featureLineIndex + 8);
      const afterFeatureStart = featureLineIndex + 8;
      const restOfText = currentDescription.substring(afterFeatureStart);
      const nextLineIndex = restOfText.indexOf('\n');
      const currentFeatures = nextLineIndex === -1 
        ? restOfText.trim() 
        : restOfText.substring(0, nextLineIndex).trim();
      const afterFeatureLine = nextLineIndex === -1 
        ? '' 
        : restOfText.substring(nextLineIndex);
      
      let newFeatures = '';
      if (currentFeatures === '') {
        newFeatures = feature;
      } else {
        const featuresArray = currentFeatures.split(', ').filter(f => f.trim() !== '');
        if (featuresArray.includes(feature)) {
          // 이미 있으면 제거
          newFeatures = featuresArray.filter(f => f !== feature).join(', ');
        } else {
          // 없으면 추가
          newFeatures = [...featuresArray, feature].join(', ');
        }
      }
      
      const newDescription = beforeFeature + newFeatures + afterFeatureLine;
      
      return {
        ...prev,
        description: newDescription
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formState.pricePerDay < 3000) {
      alert("하루 당 가격은 3,000원부터 가능합니다.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', formState.spaceName);
      formData.append('description', formState.description);
      formData.append('location', formState.location);
      formData.append('price_per_day', formState.pricePerDay.toString());
      formData.append('max_duration_days', formState.maxDurationDays.toString());
      formData.append('building_type_id', formState.buildingTypeId ? formState.buildingTypeId.toString() : '');

      if (formState.main_image) {
        formData.append('main_image', formState.main_image);
      }

      formState.additional_images.forEach((file) => {
        formData.append('additional_images', file);
      });

      if (formState.regionId !== null) {
        formData.append('regionId', formState.regionId.toString());
      }

      formState.tags.forEach((tagId) => {
        formData.append('tags[]', tagId.toString());
      });

      const response = await uploadSpace(formData);
      const data = await response.json();
      alert('공간이 성공적으로 등록 되었습니다!');
      navigate('/spaces');
    } catch (error) {
      console.error('Error uploading space:', error);
      alert('업로드 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100">
    {/* 로고를 박스 바깥에 위치시키기 */}
    <div className="w-full flex justify-center py-4">
      <Logo />
    </div>
    <Helmet>
      <title>공간 등록하기 | BIN-GO</title>
      <meta name="description" content="새로운 공간을 손쉽게 등록, BIN-GO에 올려보세요!" />
      <meta property="og:title" content="공간 등록하기 | BIN-GO" />
      <meta property="og:description" content="스마트하게 등록하는 내 공간." />
    </Helmet>
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center mb-6">내 공간 등록하기</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="공간 이름"
            value={formState.spaceName}
            onChange={(e) => setFormState((prev) => ({ ...prev, spaceName: e.target.value }))}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <textarea
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-40 resize-none"
            required
          />
          
          {/* 공간 특징 버튼들 */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">공간 특징 (클릭하여 추가/제거)</h3>
            <div className="flex flex-wrap gap-2">
              {spaceFeatures.map((feature) => {
                const isSelected = formState.description.includes(`공간 특징 : `) && 
                  formState.description.substring(
                    formState.description.indexOf('공간 특징 : ') + 8
                  ).includes(feature);
                
                return (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => handleFeatureClick(feature)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      isSelected 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {feature}
                  </button>
                );
              })}
            </div>
          </div>
          <select
            value={formState.regionId ?? ''}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                regionId: e.target.value ? parseInt(e.target.value, 10) : null,
              }))
            }
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">지역 선택</option>
            {availableRegions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="상세 주소"
            value={formState.location}
            onChange={(e) => setFormState((prev) => ({ ...prev, location: e.target.value }))}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />

          <hr className="my-6 border-gray-300" />

          <p className="text-sm text-gray-500 mt-4 mb-0.5">하루 당 보관/이용 요금(원)을 입력하세요.</p>
          <div className="relative">
            <input
              type="text"
              placeholder="하루 당 가격"
              value={formState.pricePerDay.toLocaleString()}
              onChange={(e) => {
                // 숫자와 콤마만 남기고 제거
                const numericValue = e.target.value.replace(/[^\d]/g, '');
                const parsedValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
                setFormState((prev) => ({
                  ...prev,
                  pricePerDay: parsedValue,
                }));
              }}
              className="w-full border border-gray-300 rounded px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {/* 커스텀 화살표 버튼들 */}
            <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col">
              <button
                type="button"
                onClick={() => {
                  const newValue = Math.max(3000, formState.pricePerDay + 100);
                  setFormState((prev) => ({ ...prev, pricePerDay: newValue }));
                }}
                className="w-6 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 text-xs leading-none"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => {
                  const newValue = Math.max(3000, formState.pricePerDay - 100);
                  setFormState((prev) => ({ ...prev, pricePerDay: newValue }));
                }}
                className="w-6 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 text-xs leading-none"
              >
                ▼
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4 mb-0.5">최대 보관/이용 가능 기간(일)을 입력하세요.</p>
          <input
            type="number"
            placeholder="최대보관 기간"
            value={formState.maxDurationDays}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                maxDurationDays: parseInt(e.target.value, 10),
              }))
            }
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <p className="text-sm text-gray-500 mt-2">대표 이미지</p>
          <input
            type="file"
            onChange={(e) => handleMainImageChange(e.target.files)}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />

          <p className="text-sm text-gray-500 mt-2">추가 이미지 (최대 3장)</p>
          <input
            type="file"
            multiple
            onChange={(e) => handleAdditionalImagesChange(e.target.files)}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={formState.buildingTypeId ?? ''}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                buildingTypeId: e.target.value ? parseInt(e.target.value, 10) : null,
              }))
            }
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">건물 유형 선택</option>
            {buildingType.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">태그</h3>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagSelection(tag.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    formState.tags.includes(tag.id) 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white rounded px-4 py-2 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            공간업로드
          </button>
        </form>
      </div>
    </div>
  );
};

export default UploadSpace;
